import React, { useState, useMemo, useEffect } from 'react';
import DatePicker from '../components/DatePicker';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Eye, EyeOff, Plus, Trash2, Star, ChevronRight, ChevronLeft,
  MapPin, Briefcase, GraduationCap, Languages, Wrench, User, CheckCircle2,
  Heart, Building2, FileText, Upload, X, AlertCircle
} from 'lucide-react';
import { authAPI } from '../services/api';
import { toast } from 'sonner';
import apiClient from '../services/api';

// Detect user's timezone from browser
const detectTimezone = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Map common IANA timezones to common abbreviations
    const tzMapping = {
      'Asia/Kolkata': 'IST',
      'Asia/Calcutta': 'IST',
      'America/New_York': 'EST',
      'America/Chicago': 'CST',
      'America/Denver': 'MST',
      'America/Los_Angeles': 'PST',
      'Europe/London': 'GMT',
      'Europe/Paris': 'CET',
      'Europe/Berlin': 'CET',
      'Asia/Singapore': 'SGT',
      'Asia/Tokyo': 'JST',
      'Asia/Shanghai': 'CST',
      'Australia/Sydney': 'AEST',
      'Pacific/Auckland': 'NZST',
      'Asia/Dubai': 'GST',
      'Asia/Hong_Kong': 'HKT'
    };
    return {
      ianaTimezone: tz,
      abbreviation: tzMapping[tz] || tz.split('/').pop().replace(/_/g, ' '),
      offset: new Date().toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop()
    };
  } catch (e) {
    return { ianaTimezone: 'UTC', abbreviation: 'GMT', offset: 'GMT+0' };
  }
};

const COMMON_TOOLS = [
  'Microsoft Excel', 'Google Sheets', 'Microsoft Word', 'Google Docs',
  'Slack', 'Zoom', 'Microsoft Teams', 'Trello', 'Asana', 'Monday.com',
  'Notion', 'Airtable', 'HubSpot', 'Salesforce', 'Mailchimp',
  'Canva', 'Adobe Photoshop', 'Figma', 'WordPress', 'Shopify',
  'QuickBooks', 'Xero', 'Zapier', 'ChatGPT', 'Calendly'
];

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'India',
  'Philippines', 'Germany', 'France', 'Brazil', 'Mexico', 'Other'
];

const GENDERS = ['Male', 'Female', 'Non-Binary', 'Prefer not to say'];

const NATIONALITIES = [
  'American', 'British', 'Canadian', 'Australian', 'Indian',
  'Filipino', 'German', 'French', 'Brazilian', 'Mexican', 'Other'
];

const Signup = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('hire');
  const [vaStep, setVaStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [detectedTimezone, setDetectedTimezone] = useState(null);
  
  // Detect timezone on mount
  useEffect(() => {
    const tz = detectTimezone();
    setDetectedTimezone(tz);
  }, []);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreedToTerms: false
  });

  const [vaData, setVaData] = useState({
    country: '',
    state: '',
    city: '',
    role_position: '',
    bio: '',
    experiences: [{ organization: '', designation: '', start_date: '', end_date: '', description: '' }],
    education: [{ institution: '', qualification: '', graduation_year: '', description: '' }],
    english_written: 0,
    english_spoken: 0,
    tools: []
  });

  const [hrDetails, setHrDetails] = useState({
    personal_email: '',
    contact_number: '',
    nationality: '',
    current_address: '',
    current_city: '',
    current_state: '',
    pin_code: '',
    permanent_address_same: true,
    permanent_address: '',
    date_of_birth: '',
    emergency_contact_name: '',
    emergency_contact_number: '',
    emergency_contact_name_2: '',
    emergency_contact_number_2: '',
    gender: ''
  });

  const [bankDetails, setBankDetails] = useState({
    pan_number: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    branch: ''
  });

  const [hrDocuments, setHrDocuments] = useState({
    pan_card: null,
    aadhaar_card: null,
    cancelled_cheque: null,
    pay_stubs: []
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleVaChange = (field, value) => {
    setVaData({ ...vaData, [field]: value });
  };

  const handleHrChange = (field, value) => {
    setHrDetails({ ...hrDetails, [field]: value });
  };

  const handleBankChange = (field, value) => {
    setBankDetails({ ...bankDetails, [field]: value });
  };

  const handleCheckboxChange = (checked) => {
    setFormData({ ...formData, agreedToTerms: checked });
  };

  const addExperience = () => {
    setVaData({
      ...vaData,
      experiences: [...vaData.experiences, { organization: '', designation: '', start_date: '', end_date: '', description: '' }]
    });
  };

  const removeExperience = (index) => {
    setVaData({ ...vaData, experiences: vaData.experiences.filter((_, i) => i !== index) });
  };

  const updateExperience = (index, field, value) => {
    const updated = [...vaData.experiences];
    updated[index] = { ...updated[index], [field]: value };
    setVaData({ ...vaData, experiences: updated });
  };

  const addEducation = () => {
    setVaData({
      ...vaData,
      education: [...vaData.education, { institution: '', qualification: '', graduation_year: '', description: '' }]
    });
  };

  const removeEducation = (index) => {
    setVaData({ ...vaData, education: vaData.education.filter((_, i) => i !== index) });
  };

  const updateEducation = (index, field, value) => {
    const updated = [...vaData.education];
    updated[index] = { ...updated[index], [field]: value };
    setVaData({ ...vaData, education: updated });
  };

  const toggleTool = (tool) => {
    const existing = vaData.tools.find(t => t.name === tool);
    if (existing) {
      setVaData({ ...vaData, tools: vaData.tools.filter(t => t.name !== tool) });
    } else {
      setVaData({ ...vaData, tools: [...vaData.tools, { name: tool, rating: 3 }] });
    }
  };

  const updateToolRating = (tool, rating) => {
    setVaData({
      ...vaData,
      tools: vaData.tools.map(t => t.name === tool ? { ...t, rating } : t)
    });
  };

  const getTopTools = () => {
    return [...vaData.tools].sort((a, b) => b.rating - a.rating).slice(0, 5);
  };

  const handleFileUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, or PDF files are allowed');
      return;
    }

    setUploadingDoc(docType);
    const fileData = {
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    };

    if (docType === 'pay_stubs') {
      setHrDocuments(prev => ({ ...prev, pay_stubs: [...prev.pay_stubs, fileData] }));
    } else {
      setHrDocuments(prev => ({ ...prev, [docType]: fileData }));
    }
    toast.success(`${file.name} uploaded successfully`);
    setUploadingDoc(null);
  };

  const removeDocument = (docType, index = null) => {
    if (docType === 'pay_stubs' && index !== null) {
      setHrDocuments(prev => ({ ...prev, pay_stubs: prev.pay_stubs.filter((_, i) => i !== index) }));
    } else {
      setHrDocuments(prev => ({ ...prev, [docType]: null }));
    }
  };

  const calculateProgress = useMemo(() => {
    let filledFields = 0;
    let totalFields = 0;

    // Step 1: Basic Info (4 fields)
    totalFields += 4;
    if (formData.name) filledFields++;
    if (formData.email) filledFields++;
    if (formData.password) filledFields++;
    if (formData.confirmPassword) filledFields++;

    // Step 2: Location & Role (5 fields)
    totalFields += 5;
    if (vaData.country) filledFields++;
    if (vaData.state) filledFields++;
    if (vaData.city) filledFields++;
    if (vaData.role_position) filledFields++;
    if (vaData.bio) filledFields++;

    // Step 3: Work Experience (2 fields)
    totalFields += 2;
    const completeExp = vaData.experiences.filter(e => e.organization && e.designation);
    if (completeExp.length > 0) filledFields += 2;

    // Step 4: Education (2 fields)
    totalFields += 2;
    const completeEdu = vaData.education.filter(e => e.institution && e.qualification);
    if (completeEdu.length > 0) filledFields += 2;

    // Step 5: Tools & Language (3 fields)
    totalFields += 3;
    if (vaData.english_written > 0) filledFields++;
    if (vaData.english_spoken > 0) filledFields++;
    if (vaData.tools.length > 0) filledFields++;

    // Step 6: HR Details (10 required fields)
    totalFields += 10;
    if (hrDetails.personal_email) filledFields++;
    if (hrDetails.contact_number) filledFields++;
    if (hrDetails.nationality) filledFields++;
    if (hrDetails.current_address) filledFields++;
    if (hrDetails.current_city) filledFields++;
    if (hrDetails.current_state) filledFields++;
    if (hrDetails.pin_code) filledFields++;
    if (hrDetails.date_of_birth) filledFields++;
    if (hrDetails.emergency_contact_name) filledFields++;
    if (hrDetails.emergency_contact_number) filledFields++;

    // Step 7: Bank Details (5 fields)
    totalFields += 5;
    if (bankDetails.pan_number) filledFields++;
    if (bankDetails.account_number) filledFields++;
    if (bankDetails.ifsc_code) filledFields++;
    if (bankDetails.bank_name) filledFields++;
    if (bankDetails.branch) filledFields++;

    // Step 8: HR Documents (3 required)
    totalFields += 3;
    if (hrDocuments.pan_card) filledFields++;
    if (hrDocuments.aadhaar_card) filledFields++;
    if (hrDocuments.cancelled_cheque) filledFields++;

    return Math.round((filledFields / totalFields) * 100);
  }, [formData, vaData, hrDetails, bankDetails, hrDocuments]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!formData.agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const registrationData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: activeTab === 'hire' ? 'client' : 'va',
        // Auto-detect timezone from browser
        working_timezone: detectedTimezone?.abbreviation || 'GMT',
        timezone_iana: detectedTimezone?.ianaTimezone || 'UTC'
      };

      if (activeTab === 'work') {
        registrationData.va_profile = {
          location: { country: vaData.country, state: vaData.state, city: vaData.city },
          role_position: vaData.role_position,
          bio: vaData.bio,
          experiences: vaData.experiences.filter(e => e.organization),
          education: vaData.education.filter(e => e.institution),
          language_proficiency: { english_written: vaData.english_written, english_spoken: vaData.english_spoken },
          tools: vaData.tools,
          top_tools: getTopTools(),
          hr_details: {
            personal_email: hrDetails.personal_email,
            contact_number: hrDetails.contact_number,
            nationality: hrDetails.nationality,
            current_address: hrDetails.current_address,
            current_city: hrDetails.current_city,
            current_state: hrDetails.current_state,
            pin_code: hrDetails.pin_code,
            permanent_address: hrDetails.permanent_address_same ? hrDetails.current_address : hrDetails.permanent_address,
            date_of_birth: hrDetails.date_of_birth,
            emergency_contact_name: hrDetails.emergency_contact_name,
            emergency_contact_number: hrDetails.emergency_contact_number,
            emergency_contact_name_2: hrDetails.emergency_contact_name_2,
            emergency_contact_number_2: hrDetails.emergency_contact_number_2,
            gender: hrDetails.gender
          },
          bank_details: {
            pan_number: bankDetails.pan_number,
            account_number: bankDetails.account_number,
            ifsc_code: bankDetails.ifsc_code,
            bank_name: bankDetails.bank_name,
            branch: bankDetails.branch
          },
          documents_uploaded: {
            pan_card: hrDocuments.pan_card?.name || null,
            aadhaar_card: hrDocuments.aadhaar_card?.name || null,
            cancelled_cheque: hrDocuments.cancelled_cheque?.name || null,
            pay_stubs: hrDocuments.pay_stubs.map(p => p.name)
          }
        };
      }

      const response = await authAPI.register(registrationData);
      
      // Store auth data first
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('isAuthenticated', 'true');
      
      // Upload documents in background if VA registration
      if (activeTab === 'work' && response.data.user) {
        const token = response.data.access_token;
        
        // Upload documents in background - don't block success message
        const uploadDocuments = async () => {
          try {
            if (hrDocuments.pan_card?.file) {
              const fd = new FormData();
              fd.append('file', hrDocuments.pan_card.file);
              fd.append('document_type', 'pan_card');
              await apiClient.post('/profile/upload-document', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            }
            
            if (hrDocuments.aadhaar_card?.file) {
              const fd = new FormData();
              fd.append('file', hrDocuments.aadhaar_card.file);
              fd.append('document_type', 'aadhaar_card');
              await apiClient.post('/profile/upload-document', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            }
            
            if (hrDocuments.cancelled_cheque?.file) {
              const fd = new FormData();
              fd.append('file', hrDocuments.cancelled_cheque.file);
              fd.append('document_type', 'cancelled_cheque');
              await apiClient.post('/profile/upload-document', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            }

            for (const payStub of hrDocuments.pay_stubs) {
              if (payStub.file) {
                const fd = new FormData();
                fd.append('file', payStub.file);
                fd.append('document_type', 'pay_stub');
                await apiClient.post('/profile/upload-document', fd, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                });
              }
            }
          } catch (error) {
            console.error('Document upload error:', error);
          }
        };
        
        // Start uploads in background
        uploadDocuments();
      }

      toast.success('Account created successfully!');
      setLoading(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Signup failed:', error);
      let errorMessage = 'Signup failed. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid registration data. Please check your inputs.';
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your network.';
      }
      
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const validateVaStep = (step) => {
    switch (step) {
      case 1: return formData.name && formData.email && formData.password && formData.confirmPassword;
      case 2: return vaData.country && vaData.role_position && vaData.bio && vaData.bio.length <= 200;
      case 3: return vaData.experiences.some(e => e.organization && e.designation);
      case 4: return vaData.education.some(e => e.institution && e.qualification);
      case 5: return vaData.tools.length >= 1;
      case 6: return hrDetails.personal_email && hrDetails.contact_number && hrDetails.nationality && 
                     hrDetails.current_address && hrDetails.current_city && hrDetails.current_state && 
                     hrDetails.pin_code && hrDetails.date_of_birth && hrDetails.emergency_contact_name && 
                     hrDetails.emergency_contact_number && hrDetails.gender;
      case 7: return bankDetails.pan_number && bankDetails.account_number && bankDetails.ifsc_code && 
                     bankDetails.bank_name && bankDetails.branch;
      case 8: return hrDocuments.pan_card && hrDocuments.aadhaar_card && hrDocuments.cancelled_cheque;
      default: return true;
    }
  };

  const nextVaStep = () => {
    if (validateVaStep(vaStep)) {
      setVaStep(vaStep + 1);
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const renderStarRating = (currentRating, onChange) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <Star
          key={star}
          className={`w-5 h-5 cursor-pointer transition-colors ${
            star <= currentRating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300 hover:text-yellow-400'
          }`}
          onClick={() => onChange(star)}
        />
      ))}
    </div>
  );

  const renderClientForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Full Name</Label>
        <Input name="name" placeholder="Enter your full name" value={formData.name} onChange={handleChange} required data-testid="signup-name-input" />
      </div>
      <div className="space-y-2">
        <Label>Email Address</Label>
        <Input name="email" type="email" placeholder="Enter your email" value={formData.email} onChange={handleChange} required data-testid="signup-email-input" />
      </div>
      <div className="space-y-2">
        <Label>Password</Label>
        <div className="relative">
          <Input name="password" type={showPassword ? 'text' : 'password'} placeholder="Min 6 characters" value={formData.password} onChange={handleChange} required data-testid="signup-password-input" />
          <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Confirm Password</Label>
        <Input name="confirmPassword" type="password" placeholder="Confirm your password" value={formData.confirmPassword} onChange={handleChange} required data-testid="signup-confirm-password-input" />
      </div>
      <div className="flex items-center space-x-2 py-2">
        <Checkbox id="terms" checked={formData.agreedToTerms} onCheckedChange={handleCheckboxChange} data-testid="signup-terms-checkbox" />
        <label htmlFor="terms" className="text-sm text-gray-700">
          I agree to the <a href="https://withnimble.com/terms/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Terms of Use</a> and <a href="https://withnimble.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Privacy Policy</a>
        </label>
      </div>
      <Button type="submit" className="w-full bg-[#5FD068] hover:bg-[#4ec057] text-white py-6" disabled={!formData.agreedToTerms || loading} data-testid="signup-submit-btn">
        {loading ? 'Creating Account...' : 'Create Account'}
      </Button>
    </form>
  );

  const renderVaForm = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-600">
          <span>Step {vaStep} of 9</span>
          <span>{calculateProgress}% complete</span>
        </div>
        <Progress value={calculateProgress} className="h-2" />
      </div>

      {vaStep === 1 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-[#5FD068]" />
            <h3 className="font-semibold text-lg">Basic Information</h3>
          </div>
          <div className="space-y-2">
            <Label>Full Name *</Label>
            <Input name="name" placeholder="Enter your full name" value={formData.name} onChange={handleChange} required data-testid="va-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Email Address *</Label>
            <Input name="email" type="email" placeholder="Enter your email" value={formData.email} onChange={handleChange} required data-testid="va-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Password *</Label>
              <div className="relative">
                <Input name="password" type={showPassword ? 'text' : 'password'} placeholder="Min 6 characters" value={formData.password} onChange={handleChange} required data-testid="va-password-input" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Confirm Password *</Label>
              <Input name="confirmPassword" type="password" placeholder="Confirm password" value={formData.confirmPassword} onChange={handleChange} required data-testid="va-confirm-password-input" />
            </div>
          </div>
        </div>
      )}

      {vaStep === 2 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-[#5FD068]" />
            <h3 className="font-semibold text-lg">Location & Role</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Country *</Label>
              <select className="w-full px-3 py-2 border rounded-md" value={vaData.country} onChange={(e) => handleVaChange('country', e.target.value)} data-testid="va-country-select">
                <option value="">Select country</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>State/Province</Label>
              <Input placeholder="Enter state" value={vaData.state} onChange={(e) => handleVaChange('state', e.target.value)} data-testid="va-state-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>City</Label>
            <Input placeholder="Enter city" value={vaData.city} onChange={(e) => handleVaChange('city', e.target.value)} data-testid="va-city-input" />
          </div>
          <div className="space-y-2">
            <Label>Role/Position *</Label>
            <Input placeholder="e.g., Virtual Assistant, Executive Assistant" value={vaData.role_position} onChange={(e) => handleVaChange('role_position', e.target.value)} data-testid="va-role-input" />
          </div>
          <div className="space-y-2">
            <Label>Bio * <span className="text-gray-500 text-sm">({vaData.bio.length}/200 characters)</span></Label>
            <Textarea placeholder="Write a brief bio about yourself." value={vaData.bio} onChange={(e) => handleVaChange('bio', e.target.value.slice(0, 200))} rows={3} className={vaData.bio.length > 200 ? 'border-red-500' : ''} data-testid="va-bio-input" />
          </div>
        </div>
      )}

      {vaStep === 3 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#5FD068]" />
              <h3 className="font-semibold text-lg">Work Experience</h3>
            </div>
            <Button variant="outline" size="sm" onClick={addExperience} data-testid="add-experience-btn">
              <Plus className="w-4 h-4 mr-1" /> Add More
            </Button>
          </div>
          <div className="space-y-6 max-h-96 overflow-y-auto pr-2">
            {vaData.experiences.map((exp, index) => (
              <div key={index} className="p-4 border rounded-lg bg-gray-50 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm text-gray-700">Experience {index + 1}</span>
                  {vaData.experiences.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeExperience(index)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Organization *</Label>
                    <Input placeholder="Company name" value={exp.organization} onChange={(e) => updateExperience(index, 'organization', e.target.value)} data-testid={`exp-org-${index}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Designation *</Label>
                    <Input placeholder="Job title" value={exp.designation} onChange={(e) => updateExperience(index, 'designation', e.target.value)} data-testid={`exp-designation-${index}`} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Start Date</Label>
                    <Input type="month" value={exp.start_date} onChange={(e) => updateExperience(index, 'start_date', e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">End Date</Label>
                    <Input type="month" value={exp.end_date} onChange={(e) => updateExperience(index, 'end_date', e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Textarea placeholder="Brief description" value={exp.description} onChange={(e) => updateExperience(index, 'description', e.target.value)} rows={2} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {vaStep === 4 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-[#5FD068]" />
              <h3 className="font-semibold text-lg">Education</h3>
            </div>
            <Button variant="outline" size="sm" onClick={addEducation} data-testid="add-education-btn">
              <Plus className="w-4 h-4 mr-1" /> Add More
            </Button>
          </div>
          <div className="space-y-6 max-h-96 overflow-y-auto pr-2">
            {vaData.education.map((edu, index) => (
              <div key={index} className="p-4 border rounded-lg bg-gray-50 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm text-gray-700">Education {index + 1}</span>
                  {vaData.education.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeEducation(index)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Institution *</Label>
                    <Input placeholder="University/College" value={edu.institution} onChange={(e) => updateEducation(index, 'institution', e.target.value)} data-testid={`edu-institution-${index}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Qualification *</Label>
                    <Input placeholder="Degree/Certificate" value={edu.qualification} onChange={(e) => updateEducation(index, 'qualification', e.target.value)} data-testid={`edu-qualification-${index}`} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Graduation Year</Label>
                  <Input type="number" placeholder="e.g., 2020" min="1950" max="2030" value={edu.graduation_year} onChange={(e) => updateEducation(index, 'graduation_year', e.target.value)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {vaStep === 5 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Languages className="w-5 h-5 text-[#5FD068]" />
            <h3 className="font-semibold text-lg">Language & Tools</h3>
          </div>
          <div className="p-4 border rounded-lg bg-gray-50">
            <p className="font-medium text-sm text-gray-700 mb-3">English Proficiency</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Written</Label>
                {renderStarRating(vaData.english_written, (r) => handleVaChange('english_written', r))}
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Spoken</Label>
                {renderStarRating(vaData.english_spoken, (r) => handleVaChange('english_spoken', r))}
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-[#5FD068]" />
              <p className="font-medium text-sm">Select & Rate Your Tools *</p>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-2">
              {COMMON_TOOLS.map(tool => {
                const selected = vaData.tools.find(t => t.name === tool);
                return (
    <div key={tool} className={`p-2 border rounded-lg cursor-pointer transition-all ${selected ? 'bg-green-50 border-[#5FD068]' : 'hover:bg-gray-50'}`} onClick={() => toggleTool(tool)}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{tool}</span>
                      {selected && <Badge variant="outline" className="text-xs">{selected.rating}/5</Badge>}
                    </div>
                  </div>
    );
              })}
            </div>
            {vaData.tools.length > 0 && (
              <div className="mt-4 p-3 border rounded-lg">
                <p className="text-sm font-medium mb-2">Rate your selected tools:</p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {vaData.tools.map(tool => (
                    <div key={tool.name} className="flex items-center justify-between">
                      <span className="text-sm">{tool.name}</span>
                      {renderStarRating(tool.rating, (r) => updateToolRating(tool.name, r))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {vaStep === 6 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-[#5FD068]" />
            <h3 className="font-semibold text-lg">HR Details</h3>
          </div>
          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Personal Email ID *</Label>
                <Input type="email" placeholder="personal@email.com" value={hrDetails.personal_email} onChange={(e) => handleHrChange('personal_email', e.target.value)} data-testid="hr-personal-email" />
              </div>
              <div className="space-y-2">
                <Label>Contact Number *</Label>
                <Input type="tel" placeholder="+91 XXXXX XXXXX" value={hrDetails.contact_number} onChange={(e) => handleHrChange('contact_number', e.target.value)} data-testid="hr-contact-number" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nationality *</Label>
                <select className="w-full px-3 py-2 border rounded-md" value={hrDetails.nationality} onChange={(e) => handleHrChange('nationality', e.target.value)} data-testid="hr-nationality">
                  <option value="">Select nationality</option>
                  {NATIONALITIES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Gender *</Label>
                <select className="w-full px-3 py-2 border rounded-md" value={hrDetails.gender} onChange={(e) => handleHrChange('gender', e.target.value)} data-testid="hr-gender">
                  <option value="">Select gender</option>
                  {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date of Birth *</Label>
              <DatePicker value={hrDetails.date_of_birth} onChange={(date) => handleHrChange('date_of_birth', date)} placeholder="Select date of birth" maxDate={new Date()} data-testid="hr-dob" />
            </div>
            <div className="space-y-2">
              <Label>Current Address *</Label>
              <Textarea placeholder="Enter your current address" value={hrDetails.current_address} onChange={(e) => handleHrChange('current_address', e.target.value)} rows={2} data-testid="hr-current-address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Current City *</Label>
                <Input placeholder="City" value={hrDetails.current_city} onChange={(e) => handleHrChange('current_city', e.target.value)} data-testid="hr-current-city" />
              </div>
              <div className="space-y-2">
                <Label>Current State *</Label>
                <Input placeholder="State" value={hrDetails.current_state} onChange={(e) => handleHrChange('current_state', e.target.value)} data-testid="hr-current-state" />
              </div>
              <div className="space-y-2">
                <Label>Pin Code *</Label>
                <Input placeholder="XXXXXX" value={hrDetails.pin_code} onChange={(e) => handleHrChange('pin_code', e.target.value)} data-testid="hr-pin-code" />
              </div>
            </div>
            <div className="flex items-center space-x-2 py-2">
              <Checkbox id="permanent-same" checked={hrDetails.permanent_address_same} onCheckedChange={(checked) => handleHrChange('permanent_address_same', checked)} />
              <label htmlFor="permanent-same" className="text-sm text-gray-700">Permanent address is same as current address</label>
            </div>
            {!hrDetails.permanent_address_same && (
              <div className="space-y-2">
                <Label>Permanent Address</Label>
                <Textarea placeholder="Enter your permanent address" value={hrDetails.permanent_address} onChange={(e) => handleHrChange('permanent_address', e.target.value)} rows={2} data-testid="hr-permanent-address" />
              </div>
            )}
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="font-medium text-sm text-orange-800 mb-3">Emergency Contacts</p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="space-y-2">
                  <Label className="text-xs">Contact Name 1 *</Label>
                  <Input placeholder="Full name" value={hrDetails.emergency_contact_name} onChange={(e) => handleHrChange('emergency_contact_name', e.target.value)} data-testid="hr-emergency-name-1" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Contact Number 1 *</Label>
                  <Input type="tel" placeholder="+91 XXXXX XXXXX" value={hrDetails.emergency_contact_number} onChange={(e) => handleHrChange('emergency_contact_number', e.target.value)} data-testid="hr-emergency-number-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Contact Name 2 (Optional)</Label>
                  <Input placeholder="Full name" value={hrDetails.emergency_contact_name_2} onChange={(e) => handleHrChange('emergency_contact_name_2', e.target.value)} data-testid="hr-emergency-name-2" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Contact Number 2 (Optional)</Label>
                  <Input type="tel" placeholder="+91 XXXXX XXXXX" value={hrDetails.emergency_contact_number_2} onChange={(e) => handleHrChange('emergency_contact_number_2', e.target.value)} data-testid="hr-emergency-number-2" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {vaStep === 7 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-[#5FD068]" />
            <h3 className="font-semibold text-lg">Bank Account Details</h3>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <p className="text-sm text-blue-700">Your bank details are securely stored and used only for salary disbursement purposes.</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>PAN Number *</Label>
              <Input placeholder="ABCDE1234F" value={bankDetails.pan_number} onChange={(e) => handleBankChange('pan_number', e.target.value.toUpperCase())} maxLength={10} data-testid="bank-pan" />
            </div>
            <div className="space-y-2">
              <Label>Account Number *</Label>
              <Input placeholder="Enter your bank account number" value={bankDetails.account_number} onChange={(e) => handleBankChange('account_number', e.target.value)} data-testid="bank-account" />
            </div>
            <div className="space-y-2">
              <Label>IFSC Code *</Label>
              <Input placeholder="SBIN0001234" value={bankDetails.ifsc_code} onChange={(e) => handleBankChange('ifsc_code', e.target.value.toUpperCase())} maxLength={11} data-testid="bank-ifsc" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bank Name *</Label>
                <Input placeholder="e.g., State Bank of India" value={bankDetails.bank_name} onChange={(e) => handleBankChange('bank_name', e.target.value)} data-testid="bank-name" />
              </div>
              <div className="space-y-2">
                <Label>Branch *</Label>
                <Input placeholder="Branch name" value={bankDetails.branch} onChange={(e) => handleBankChange('branch', e.target.value)} data-testid="bank-branch" />
              </div>
            </div>
          </div>
        </div>
      )}

      {vaStep === 8 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#5FD068]" />
            <h3 className="font-semibold text-lg">HR Documents</h3>
          </div>
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
            <div className="flex gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              <div className="text-sm text-yellow-700">
                <p className="font-medium">Upload Guidelines:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Accepted formats: JPG, PNG, PDF</li>
                  <li>Maximum file size: 5MB per document</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-medium">PAN Card *</Label>
                {hrDocuments.pan_card && <Badge variant="outline" className="text-green-600">Uploaded</Badge>}
              </div>
              {hrDocuments.pan_card ? (
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm truncate">{hrDocuments.pan_card.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeDocument('pan_card')}><X className="w-4 h-4 text-red-500" /></Button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Click to upload PAN Card</span>
                  <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => handleFileUpload(e, 'pan_card')} data-testid="upload-pan" />
                </label>
              )}
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-medium">Aadhaar Card / National ID *</Label>
                {hrDocuments.aadhaar_card && <Badge variant="outline" className="text-green-600">Uploaded</Badge>}
              </div>
              {hrDocuments.aadhaar_card ? (
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm truncate">{hrDocuments.aadhaar_card.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeDocument('aadhaar_card')}><X className="w-4 h-4 text-red-500" /></Button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Click to upload Aadhaar/National ID</span>
                  <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => handleFileUpload(e, 'aadhaar_card')} data-testid="upload-aadhaar" />
                </label>
              )}
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-medium">Cancelled Cheque *</Label>
                {hrDocuments.cancelled_cheque && <Badge variant="outline" className="text-green-600">Uploaded</Badge>}
              </div>
              {hrDocuments.cancelled_cheque ? (
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm truncate">{hrDocuments.cancelled_cheque.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeDocument('cancelled_cheque')}><X className="w-4 h-4 text-red-500" /></Button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Click to upload Cancelled Cheque</span>
                  <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => handleFileUpload(e, 'cancelled_cheque')} data-testid="upload-cheque" />
                </label>
              )}
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-medium">Previous Employer Pay Stubs (Optional)</Label>
                {hrDocuments.pay_stubs.length > 0 && <Badge variant="outline" className="text-green-600">{hrDocuments.pay_stubs.length} uploaded</Badge>}
              </div>
              {hrDocuments.pay_stubs.length > 0 && (
                <div className="space-y-2 mb-3">
                  {hrDocuments.pay_stubs.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm truncate">{doc.name}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeDocument('pay_stubs', index)}><X className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">Click to upload Pay Stubs</span>
                <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => handleFileUpload(e, 'pay_stubs')} data-testid="upload-paystubs" />
              </label>
            </div>
          </div>
        </div>
      )}

      {vaStep === 9 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-[#5FD068]" />
            <h3 className="font-semibold text-lg">Review & Submit</h3>
          </div>
          <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
            <div className="p-3 border rounded-lg">
              <p className="font-medium text-sm text-gray-700 mb-2">Basic Information</p>
              <p className="text-sm"><span className="text-gray-500">Name:</span> {formData.name}</p>
              <p className="text-sm"><span className="text-gray-500">Email:</span> {formData.email}</p>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="font-medium text-sm text-gray-700 mb-2">Location & Role</p>
              <p className="text-sm"><span className="text-gray-500">Location:</span> {vaData.city}, {vaData.state}, {vaData.country}</p>
              <p className="text-sm"><span className="text-gray-500">Role:</span> {vaData.role_position}</p>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="font-medium text-sm text-gray-700 mb-2">Work Experience ({vaData.experiences.filter(e => e.organization).length})</p>
              {vaData.experiences.filter(e => e.organization).map((exp, i) => (
                <p key={i} className="text-sm">{exp.designation} at {exp.organization}</p>
              ))}
            </div>
            <div className="p-3 border rounded-lg">
              <p className="font-medium text-sm text-gray-700 mb-2">HR Details</p>
              <p className="text-sm"><span className="text-gray-500">Personal Email:</span> {hrDetails.personal_email}</p>
              <p className="text-sm"><span className="text-gray-500">Contact:</span> {hrDetails.contact_number}</p>
              <p className="text-sm"><span className="text-gray-500">DOB:</span> {hrDetails.date_of_birth}</p>
              <p className="text-sm"><span className="text-gray-500">Gender:</span> {hrDetails.gender}</p>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="font-medium text-sm text-gray-700 mb-2">Bank Account</p>
              <p className="text-sm"><span className="text-gray-500">PAN:</span> {bankDetails.pan_number}</p>
              <p className="text-sm"><span className="text-gray-500">Account:</span> ****{bankDetails.account_number.slice(-4)}</p>
              <p className="text-sm"><span className="text-gray-500">Bank:</span> {bankDetails.bank_name}</p>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="font-medium text-sm text-gray-700 mb-2">Documents</p>
              <p className="text-sm"><span className="text-gray-500">PAN Card:</span> {hrDocuments.pan_card ? 'Yes' : 'No'}</p>
              <p className="text-sm"><span className="text-gray-500">Aadhaar/ID:</span> {hrDocuments.aadhaar_card ? 'Yes' : 'No'}</p>
              <p className="text-sm"><span className="text-gray-500">Cancelled Cheque:</span> {hrDocuments.cancelled_cheque ? 'Yes' : 'No'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 py-2">
            <Checkbox id="terms" checked={formData.agreedToTerms} onCheckedChange={handleCheckboxChange} data-testid="va-terms-checkbox" />
            <label htmlFor="terms" className="text-sm text-gray-700">
              I agree to the <a href="https://withnimble.com/terms/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Terms of Use</a> and <a href="https://withnimble.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Privacy Policy</a>
            </label>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-4 border-t">
        {vaStep > 1 ? (
          <Button variant="outline" onClick={() => setVaStep(vaStep - 1)}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
        ) : <div />}

        {vaStep < 9 ? (
          <Button className="bg-[#5FD068] hover:bg-[#4ec057]" onClick={nextVaStep}>
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button className="bg-[#5FD068] hover:bg-[#4ec057]" onClick={handleSubmit} disabled={!formData.agreedToTerms || loading} data-testid="va-submit-btn">
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <header className="bg-white border-b border-[#E5E7EB] px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_virtual-admin-1/artifacts/zwdakbm3_New%20Logo%20HD.png" 
              alt="Nimble Assistants" 
              className="h-12"
            />
          </Link>
          <Link to="/login">
            <Button variant="outline" className="border-[#E5E7EB] hover:border-[#5FD068] hover:text-[#4BB854]">Sign In</Button>
          </Link>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center py-8 px-4">
        <Card className="w-full max-w-xl border border-[#E5E7EB]">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-medium tracking-tight text-[#0A0D0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Create Account</CardTitle>
            <p className="text-[#4B5563] text-sm mt-1">Join Nimble Assistants and transform your productivity</p>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setVaStep(1); }}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="hire" className="text-sm">I want to Hire a VA</TabsTrigger>
                <TabsTrigger value="work" className="text-sm">I want to Work Remotely</TabsTrigger>
              </TabsList>
              <TabsContent value="hire">{renderClientForm()}</TabsContent>
              <TabsContent value="work">{renderVaForm()}</TabsContent>
            </Tabs>
            <div className="pt-6 border-t border-[#E5E7EB] mt-6 text-center">
              <p className="text-sm text-[#4B5563]">
                Already have an account? <Link to="/login" className="text-[#5FD068] hover:text-[#4BB854] font-medium">Sign in here</Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Signup;
